import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import type { ScrollDepthPercent } from '../element-tracking/element-tracking.types';

/**
 * ScrollDepthService (Client)
 *
 * Tracks how far the user has scrolled on the page as a percentage.
 *
 * Features:
 * - Lazy initialization (only starts monitoring when first accessed)
 * - Throttled scroll event listening for performance (100ms)
 * - SSR-safe (uses isPlatformBrowser checks)
 * - Returns both exact percentage and rounded values (0, 25, 50, 75, 100)
 *
 * Formula: (scrollY) / (scrollable height) * 100
 *
 * @example
 * constructor(private scrollDepthService: ScrollDepthService) {}
 *
 * // Get current scroll depth
 * const depth = this.scrollDepthService.getCurrentScrollDepth();
 *
 * // Get rounded to nearest 25%
 * const roundedDepth = this.scrollDepthService.getCurrentScrollDepth(true);
 */
@Injectable({
  providedIn: 'root',
})
export class ScrollDepthService implements OnDestroy {
  private isBrowser: boolean;
  private currentScrollDepth = 0;
  private isMonitoring = false;
  private isDestroyed = false;
  private throttleTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly THROTTLE_MS = 100;
  private handleScrollBound: (() => void) | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object, @Inject(DOCUMENT) private document: Document) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    // handleScrollBound is lazily bound in startMonitoring to avoid unnecessary work
  }

  /**
   * Get the current scroll depth percentage
   *
   * Performance optimized: Returns cached value updated by scroll listener
   * instead of recalculating on every call
   *
   * @param rounded - If true, returns nearest 25% increment (0, 25, 50, 75, 100)
   * @returns Scroll depth percentage, or 0 if not in browser
   */
  getCurrentScrollDepth(rounded = false): number {
    if (!this.isBrowser) {
      return 0;
    }

    // Lazy initialization - start monitoring on first call (calculates initial value)
    if (!this.isMonitoring) {
      this.startMonitoring();
    }

    // Return cached value (kept up-to-date by throttled scroll listener)
    // No need to recalculate - saves DOM measurements on every interaction

    if (rounded) {
      // Type-safe return from roundToNearestQuarter
      return this.roundToNearestQuarter(this.currentScrollDepth);
    }

    return Math.round(this.currentScrollDepth);
  }

  /**
   * Start monitoring scroll events with throttling
   */
  private startMonitoring(): void {
    if (!this.isBrowser || this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.calculateScrollDepth();

    // Lazy bind handleScroll - only create bound function when actually needed
    if (!this.handleScrollBound) {
      this.handleScrollBound = this.handleScroll.bind(this);
    }

    // Set up throttled scroll listener
    this.document.defaultView?.addEventListener('scroll', this.handleScrollBound, { passive: true });
  }

  /**
   * Handle scroll events with throttling to avoid performance issues
   */
  private handleScroll(): void {
    if (this.throttleTimeout) {
      return;
    }

    this.throttleTimeout = setTimeout(() => {
      // Guard against executing after service destruction
      if (this.isDestroyed) {
        return;
      }
      this.calculateScrollDepth();
      this.throttleTimeout = null;
    }, this.THROTTLE_MS);
  }

  /**
   * Calculate the current scroll depth percentage
   * Formula: (scrollY) / (total scrollable height) * 100
   */
  private calculateScrollDepth(): void {
    if (!this.isBrowser || !this.document.defaultView) {
      return;
    }

    const window = this.document.defaultView;
    const scrollY = window.scrollY; // scrollY has universal support in modern browsers (2026)
    const windowHeight = window.innerHeight;
    const documentHeight = this.document.documentElement.scrollHeight;

    // Calculate scrollable height (total height - viewport height)
    const scrollableHeight = documentHeight - windowHeight;

    if (scrollableHeight <= 0) {
      // Page is not scrollable - entire content is visible, so user has "seen" 100%
      this.currentScrollDepth = 100;
      return;
    }

    // Calculate percentage and clamp between 0 and 100
    this.currentScrollDepth = Math.max(0, Math.min(100, (scrollY / scrollableHeight) * 100));
  }

  /**
   * Round scroll depth to nearest 25% increment (0, 25, 50, 75, 100)
   * Explicitly clamps to valid ScrollDepthPercent values for type safety
   *
   * @param value - Input percentage (should be 0-100 from calculateScrollDepth)
   * @returns One of: 0, 25, 50, 75, or 100
   */
  private roundToNearestQuarter(value: number): ScrollDepthPercent {
    const rounded = Math.round(value / 25) * 25;
    // Defensive clamping to ensure value is exactly one of: 0, 25, 50, 75, 100
    return Math.max(0, Math.min(100, rounded)) as ScrollDepthPercent;
  }

  /**
   * Clean up event listeners on destroy
   * Sets isDestroyed flag to prevent pending throttled callbacks from executing
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.isBrowser && this.document.defaultView && this.handleScrollBound) {
      this.document.defaultView.removeEventListener('scroll', this.handleScrollBound);
    }

    if (this.throttleTimeout) {
      clearTimeout(this.throttleTimeout);
      this.throttleTimeout = null;
    }
  }
}
